/* global System:readonly */
import { ComponentType } from "react";

import { EmptyArray, General } from "capitalrx-components-2.0";
import { checkPermission } from "capitalrx-shell-auth";
import "systemjs";

import { AsyncWrapper } from "../AsyncWrapper";
import { OwnerModuleContext } from "../context";
import { ModuleInfo, ModuleManifest, ModuleMetadata, Subscription } from "../model";

/**
 * Registry of module metadata indexed by module ID.
 * Used to provide metadata to the HotKeys permission system.
 */
const moduleMetadataRegistry: Map<string, ModuleMetadata> = new Map();

/**
 * Gets the metadata for a specific module.
 * @param moduleId The module ID to get metadata for.
 * @returns The module metadata or undefined if not found.
 */
export function getModuleMetadata(moduleId: string): ModuleMetadata | undefined {
  return moduleMetadataRegistry.get(moduleId);
}

/**
 * Sets the current module metadata for HotKeys permission system.
 * Called automatically when modules are loaded and when navigating between modules.
 * @param moduleId The module ID to activate.
 */
export function activateModuleMetadata(moduleId: string): void {
  const metadata = moduleMetadataRegistry.get(moduleId);
  if (metadata) {
    General.setModuleMetadata(metadata);
  } else {
    // Clear metadata if module not found (safety fallback)
    General.clearModuleMetadata();
  }
}

/**
 * Removes metadata for a specific module from the registry.
 * Useful when modules are unloaded to prevent memory leaks.
 * @param moduleId The module ID to remove.
 * @returns True if the module was found and removed, false otherwise.
 */
export function removeModuleMetadata(moduleId: string): boolean {
  return moduleMetadataRegistry.delete(moduleId);
}

/**
 * Clears all module metadata from the registry.
 * Useful for testing or when resetting the application state.
 */
export function clearModuleMetadataRegistry(): void {
  moduleMetadataRegistry.clear();
}

/**
 * The default provider function.
 * @returns An empty array.
 */
export function DefaultProviderFunction(): ReadonlyArray<Subscription> {
  return EmptyArray as ReadonlyArray<Subscription>;
}

/** A channel subscription table. */
export interface ChannelSubscriptionTable {
  /** Gets the channels registered for the specified module ID. */
  readonly [moduleId: string]: {
    /** Gets the elements registered for the specified channel ID. */
    readonly [channelId: string]: ReadonlyArray<Subscription>;
  };
}

/** Asynchronously waits for the next JS execution window. */
async function tick(): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve();
    }, 0);
  });
}

/**
 * Gets the manifests for the specified modules.
 * @param context The context the manifests are being loaded in.
 * @param modules The modules to get manifests for.
 * @param shellPermissions The current set of shell permissions.
 */
async function getManifests(
  context: "core" | "app",
  modules: ReadonlyArray<string>,
  shellPermissions: ReadonlyArray<string> | null,
): Promise<ReadonlyArray<ModuleManifest>> {
  // If we're in the application context, but we have no permissions, don't load application modules.
  if (context === "app" && (!shellPermissions || shellPermissions.length === 0)) return [];

  function errHandle(context: "core" | "app", message?: string, err?: unknown): null {
    if (context === "core") {
      if (message) console.error(message);
      if (err) console.error(err);
      throw new Error(message || "Core module manifest failure");
    }

    return null;
  }

  // Load the manifests.
  const manifests = await Promise.all(
    modules.map(async (modulePath) => {
      try {
        const basePath = modulePath.replace("index.js", "");
        const metadataPath = `${basePath}metadata.json`;

        try {
          const response = await fetch(metadataPath);

          // Anything other than a not found or an OK response results in not loading.
          if (response.status !== 404 && !response.ok) {
            return errHandle(context, `Module metadata request failed for [${metadataPath}] (status ${response.status}).`);
          }

          if (response.ok) {
            // Only bother checking if there was a metadata file.
            try {
              const metadata = (await response.json()) as ModuleMetadata;
              const { permissions, id } = metadata;

              // Store metadata in registry for HotKeys permission system
              if (id) {
                moduleMetadataRegistry.set(id, metadata);
              }

              if (permissions && permissions.length > 0) {
                if (shellPermissions && !checkPermission(shellPermissions, permissions)) {
                  return errHandle(context, `Module at [${modulePath}] failed permission check.`);
                }
              }
            } catch {
              console.warn(`Module metadata at [${metadataPath}] could not be parsed.`);
            }
          }
        } catch (err) {
          // Network error such as server not found results in not loading.
          return errHandle(context, `Module metadata fetch failed for [${metadataPath}].`, err);
        }

        // For some reason fixes an issue in FF where if loading in a background tab SystemJS produces incorrect data.
        await tick();

        const importedModule = await System.import(modulePath);
        const manifest = importedModule.default as ModuleManifest;

        if (!manifest) {
          console.warn(`Module at [${modulePath}] does not have a valid manifest. Perhaps you forgot to export it?`);
          return errHandle(context, `Module at [${modulePath}] does not have a valid manifest.`);
        }

        return manifest;
      } catch (err) {
        console.warn(`Error while attempting to load module from path [${modulePath}].`);

        if (process.env.NODE_ENV === "development") {
          console.error(err);
        }

        return errHandle(context, `Error while attempting to load module from path [${modulePath}].`, err);
      }
    }),
  );

  // Filter out nulls.
  const result = manifests.filter((manifest) => manifest !== null) as Array<ModuleManifest>;

  // Sort the manifests by reverse priority.
  result.sort(({ priority: a = 0 }, { priority: b = 0 }) => b - a);

  return result;
}

/** The channel subscription provider function. */
export interface ChannelSubscriptionProvider {
  /** Gets the components registered for the specified module and channel IDs. */
  (moduleId: string | null, channelId: string): ReadonlyArray<Subscription>;
}

/**
 * Loads the specified set of modules, returning back a module table and set of extensions.
 * @param context The context the modules are being loaded in.
 * @param modules The set of modules to load.
 * @param shellPermissions The current set of shell permissions.
 */
export async function loadModules(
  context: "core" | "app",
  modules: ReadonlyArray<string> | null,
  shellPermissions: ReadonlyArray<string> | null,
): Promise<[ReadonlyArray<ModuleManifest> | null, ChannelSubscriptionProvider]> {
  if (modules === null) return [null, DefaultProviderFunction];
  if (modules.length === 0) return [[], DefaultProviderFunction];

  const manifests = await getManifests(context, modules, shellPermissions);

  let channelSubscriptions: ChannelSubscriptionTable = {};

  function subscribe(
    moduleId: string | null,
    channelId: string,
    module: ModuleInfo,
    priority: number,
    ...args: [object | ComponentType, unknown?]
  ): void {
    if (moduleId === null) moduleId = "_null_";

    if (typeof args[0] === "object") {
      const [data] = args;

      channelSubscriptions = {
        ...channelSubscriptions,
        [moduleId]: {
          ...channelSubscriptions?.[moduleId],
          [channelId]: [
            ...(channelSubscriptions?.[moduleId]?.[channelId] || []),
            {
              type: "data",
              priority,
              module,
              data,
            },
          ],
        },
      };
    } else {
      const [component, params] = args;

      channelSubscriptions = {
        ...channelSubscriptions,
        [moduleId]: {
          ...channelSubscriptions?.[moduleId],
          [channelId]: [
            ...(channelSubscriptions?.[moduleId]?.[channelId] || []),
            {
              type: "component",
              priority,
              component,
              module,
              params,
            },
          ],
        },
      };
    }
  }

  for (const manifest of manifests) {
    if (manifest === null) continue;
    const { info, version, priority = 0, container, onInit } = manifest;

    try {
      if (onInit) {
        const containerProvider = async () => {
          switch (version) {
            case 1:
            case undefined:
              return container;
            case 2:
              return await container?.();
            default:
              return undefined;
          }
        };

        const result = onInit((moduleId, channelId, ...args) => {
          if (typeof args[0] === "object") {
            const [data] = args;

            subscribe(moduleId, channelId, info, priority, data);
          } else {
            const [Component, params] = args;

            const wrappedComponent = (): JSX.Element => (
              <OwnerModuleContext.Provider value={info}>
                <AsyncWrapper resolver={containerProvider}>
                  <Component />
                </AsyncWrapper>
              </OwnerModuleContext.Provider>
            );

            subscribe(moduleId, channelId, info, priority, wrappedComponent, params);
          }
        });

        if (result instanceof Promise) await result;
      }
    } catch {
      if (context === "core") {
        throw "Core module onInit failure";
      }
    }
  }

  return [
    manifests,
    (moduleId: string | null, channelId: string): ReadonlyArray<Subscription> => {
      if (moduleId === null) moduleId = "_null_";

      const channels = channelSubscriptions[moduleId];
      if (!channels) return EmptyArray as ReadonlyArray<Subscription>;
      return channels[channelId] || (EmptyArray as ReadonlyArray<Subscription>);
    },
  ];
}
